import { FC } from 'react';

import SectionContainer from '@common/components/SectionContainer';
import { SpaEntityError } from '@common/components/SpaEntity/SpaEntityError';
import { Routes } from '@common/constants';
import { useCheckForArrivalDate } from '@common/hooks';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useSpaSearchComplete } from '@features/JourneyPickerV2/useSpaSearchComplete';
import { useTranslations } from '@lang';
import { useRouter } from 'shared-routing';

import { SpaListTemplate } from 'voyager';

import { useSpaEntityFilter } from '@features/spa/hooks/useSpaEntityFilter';
import { buildCategoryOptions } from '@features/spa/utils/buildCategoryOptions';
import { FacilitiesV2Props } from './FacilitiesV2.types';
import { useFacilitiesList } from './hooks/useFacilitiesList';

export const FacilitiesV2: FC<FacilitiesV2Props> = ({
  property,
  journeyParams,
  facilitiesCategories = [],
  categoryId = null,
  userHasAccessToFacilities = false,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.FACILITY_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
  });

  const handleSearchComplete = useSpaSearchComplete();

  const { listItems, isFetching } = useFacilitiesList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    date: filter.appliedDateString,
  });

  const facilitiesVisibleToMembers =
    property?.modulesExcludingDisabled?.spa?.features?.facilities?.features?.visibleToMembers
      ?.enabled;

  if (!userHasAccessToFacilities) {
    return (
      <SpaEntityError
        errorMessage={facilitiesVisibleToMembers ? 'app.mustBeMember' : 'app.mustBeLoggedIn'}
        values={{ spaEntity: 'facilities' }}
      />
    );
  }

  const categoryOptions = buildCategoryOptions(facilitiesCategories, t('common.allCategories'));

  const handleSelectFacility = (id: string) => {
    router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } });
  };

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="single"
            dateContext="spa"
            categories={navCategories}
            category="facilities"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <SpaListTemplate
          items={isFetching ? [] : listItems}
          onSelectItem={handleSelectFacility}
          isLoading={isFetching}
          categoryOptions={categoryOptions}
          selectedCategory={filter.selectedCategory || ''}
          onCategoryChange={filter.handleDirectCategoryChange}
          titleLabel={t('app.facilities')}
          categoryLabel={t('common.category')}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('spa.soldOut')}
          noResultsLabel={t('spa.noResults')}
          noImageLabel={t('spa.noImage')}
          formatGuests={(n) => t('common.numberOfGuests', { guestsAmount: n })}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
